import React from "react";
import { Link } from "react-router-dom";
import { Target, Eye, ArrowRight } from "lucide-react";
import { SectionHeading, SectionLabel } from "../components/SectionHeading";
import { useSite, absoluteUrl } from "../context/SiteContext";
import SmartImage from "../components/SmartImage";

export default function AboutPage() {
  const { settings } = useSite();
  const { brand, about, stats } = settings;
  return (
    <div data-testid="about-page">
      <section className="border-b border-[#1F3550] relative">
        <div className="absolute inset-0 blueprint-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <SectionLabel code="A/01">About Us</SectionLabel>
          <h1 className="mt-6 font-heading font-black text-4xl md:text-6xl uppercase tracking-tight leading-[0.95] max-w-4xl">
            {about.headline}
          </h1>
          <p className="mt-6 text-lg text-[#94A3B8] max-w-3xl leading-relaxed">
            {about.intro}
          </p>
        </div>
      </section>

      <section className="border-b border-[#1F3550]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <SectionHeading code="A/02" overline="Our Story" title="Two Decades of Heavy Industry." />
            <div className="mt-6 space-y-5 text-[#94A3B8] leading-relaxed">
              {(about.story_paragraphs || []).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            {about.story_image && (
              <div className="relative border border-[#1F3550] aspect-[4/5] overflow-hidden">
                <SmartImage src={about.story_image} alt="industrial site" imgClassName="grayscale hover:grayscale-0 transition-all duration-700" />
                <div className="absolute bottom-0 left-0 right-0 bg-[#050A0F]/90 p-5 border-t border-[#F39C12]">
                  <div className="font-mono text-[10px] tracking-[0.3em] text-[#F39C12]">EST. {brand.established}</div>
                  <div className="mt-1 font-heading font-bold text-[#F8FAFC]">{brand.city}, India</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="border-b border-[#1F3550]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 border-l border-r border-[#1F3550]">
          <div className="p-10 md:p-14 border-b md:border-b-0 md:border-r border-[#1F3550] bg-[#0D1B2A]">
            <Target className="h-8 w-8 text-[#F39C12]" />
            <SectionLabel className="mt-6" code="M/01">Mission</SectionLabel>
            <h3 className="mt-4 font-heading font-bold text-2xl md:text-3xl text-[#F8FAFC] leading-tight">
              World-class engineering & construction solutions.
            </h3>
            <p className="mt-5 text-[#94A3B8] leading-relaxed">{about.mission}</p>
          </div>
          <div className="p-10 md:p-14 bg-[#050A0F]">
            <Eye className="h-8 w-8 text-[#F39C12]" />
            <SectionLabel className="mt-6" code="V/01">Vision</SectionLabel>
            <h3 className="mt-4 font-heading font-bold text-2xl md:text-3xl text-[#F8FAFC] leading-tight">
              A trusted leader in industrial engineering.
            </h3>
            <p className="mt-5 text-[#94A3B8] leading-relaxed">{about.vision}</p>
          </div>
        </div>
      </section>

      <section className="border-b border-[#1F3550]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <SectionHeading code="A/03" overline="Core Values" title="What we refuse to compromise on." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#1F3550] border border-[#1F3550]">
            {(about.values || []).map((v, i) => (
              <div key={i} data-testid={`value-${i}`} className="bg-[#050A0F] p-8 hover:bg-[#0D1B2A] transition-colors">
                <div className="font-mono text-[10px] tracking-[0.3em] text-[#F39C12]">V/0{i + 1}</div>
                <h4 className="mt-6 font-heading font-bold text-lg text-[#F8FAFC]">{v.title}</h4>
                <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {stats.length > 0 && (
        <section className="border-b border-[#1F3550] bg-[#0D1B2A]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-2 md:grid-cols-4">
            {stats.map((s, i) => (
              <div key={i} className={`py-6 px-4 ${i !== 0 ? "md:border-l border-[#1F3550]" : ""} ${i >= 2 ? "border-t md:border-t-0 border-[#1F3550]" : ""}`}>
                <div className="font-heading font-black text-4xl md:text-5xl text-[#F39C12]">{s.value}</div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <SectionLabel code="A/04">Ready to build</SectionLabel>
            <h3 className="mt-3 font-heading font-bold text-2xl md:text-3xl uppercase">Let's discuss your next industrial project.</h3>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-7 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#D68910] transition-colors">
            Contact Us <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
